// src/services/api/support.service.ts
import { apiService } from '../api.service';

// Interfaces
export interface SupportTicket {
  id: string;
  ticketNumber: string;
  userId: string;
  type: 'SUPPORT' | 'ZENUS_ACCOUNT';
  subject: string;
  content: string;
  status: TicketStatus;
  priority: TicketPriority;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  closedAt?: string;
  assignedToId?: string;
  assignedTo?: {
    id: string;
    email: string;
    profile?: {
      firstName: string;
      lastName: string;
    };
  };
  messages?: TicketMessage[];
  attachments?: TicketAttachment[];
  _count?: {
    messages: number;
    attachments: number;
  };
}

export interface TicketMessage {
  id: string;
  ticketId: string;
  userId: string;
  message: string;
  isInternal: boolean;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    role: string;
    profile?: {
      firstName: string;
      lastName: string;
      fullName: string;
    };
  };
  attachments?: TicketAttachment[];
}

export interface TicketAttachment {
  id: string;
  ticketId?: string;
  messageId?: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  uploadedById: string;
  createdAt: string;
}

export type TicketStatus = 
  | 'PENDING'
  | 'IN_COMPLIANCE'
  | 'APPROVED'
  | 'SENT_TO_PROVIDER'
  | 'IN_PROGRESS'
  | 'RESOLVED'
  | 'REJECTED'
  | 'CLOSED';

export type TicketPriority = 
  | 'LOW'
  | 'MEDIUM'
  | 'HIGH'
  | 'CRITICAL';

export interface CreateTicketDTO {
  type: 'SUPPORT';
  subject: string;
  content: string;
  priority?: TicketPriority;
}

export interface AddMessageDTO {
  message: string;
}

export interface TicketFilters {
  type?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface TicketListResponse {
  tickets: SupportTicket[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface TicketDetailResponse {
  ticket: SupportTicket;
  messages?: TicketMessage[];
  attachments?: TicketAttachment[];
}

class SupportService {
  // Get user's tickets with filters
  async getUserTickets(filters?: TicketFilters): Promise<TicketListResponse> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const endpoint = queryString ? `/support/tickets?${queryString}` : '/support/tickets';
    
    const response = await apiService.get<any>(endpoint);
    
    // El backend envuelve la respuesta en { success: true, data: {...} }
    // Necesitamos extraer el data
    if (response && response.data) {
      return response.data;
    }
    
    return response;
  }

  // Get ticket details with messages
  async getTicketDetails(ticketId: string): Promise<TicketDetailResponse> {
    const response = await apiService.get<any>(`/support/tickets/${ticketId}`);
    
    // El backend envuelve la respuesta en { success: true, data: {...} }
    if (response && response.data) {
      return response.data;
    }
    
    return response;
  }

  // Create new support ticket
  async createTicket(data: CreateTicketDTO): Promise<{ success: boolean; data: TicketDetailResponse }> {
    // Ensure we're sending the correct data format
    const ticketData = {
      type: 'SUPPORT',
      subject: data.subject,
      content: data.content,
      priority: data.priority || 'MEDIUM'
    };
    
    return await apiService.post('/support/tickets', ticketData);
  }

  // Add message to ticket
  async addMessage(ticketId: string, data: AddMessageDTO): Promise<{ success: boolean; message: string }> {
    return await apiService.post(`/support/tickets/${ticketId}/messages`, data);
  }

  // Upload attachments with ticket
  // Backend now supports file uploads - up to 5 files, 5MB each
  async createTicketWithAttachments(
    data: CreateTicketDTO,
    files: File[]
  ): Promise<{ success: boolean; data: TicketDetailResponse }> {
    const formData = new FormData();

    // Append ticket data
    formData.append('type', 'SUPPORT');
    formData.append('subject', data.subject);
    formData.append('content', data.content);
    if (data.priority) {
      formData.append('priority', data.priority);
    }

    // Append files (max 5 files, 5MB each - validated in backend)
    files.forEach((file) => {
      formData.append('attachments', file);
    });

    return await apiService.upload('/support/tickets', formData);
  }

  // Add message with attachments
  // Backend now supports file uploads - up to 5 files, 5MB each
  async addMessageWithAttachments(
    ticketId: string,
    message: string,
    files: File[]
  ): Promise<{ success: boolean; message: string }> {
    const formData = new FormData();

    formData.append('message', message);

    // Append files (max 5 files, 5MB each - validated in backend)
    files.forEach((file) => {
      formData.append('attachments', file);
    });

    return await apiService.upload(`/support/tickets/${ticketId}/messages`, formData);
  }

  // Get ticket attachment URL (gets signed URL from backend)
  async getAttachmentUrl(attachmentId: string): Promise<string> {
    try {
      const response = await apiService.get<any>(`/support/tickets/attachments/${attachmentId}`);

      if (response && response.data && response.data.url) {
        return response.data.url;
      }

      throw new Error('Failed to get attachment URL');
    } catch (error) {
      console.error('Error getting attachment URL:', error);
      throw error;
    }
  }

  // Download attachment (opens in new tab)
  async downloadAttachment(attachmentId: string, fileName: string): Promise<void> {
    try {
      const url = await this.getAttachmentUrl(attachmentId);

      // Open in new tab
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw error;
    }
  }

  // Helper to get status color
  getStatusColor(status: TicketStatus): string {
    switch (status) {
      case 'PENDING':
        return '#EAB308'; // yellow-500
      case 'IN_PROGRESS':
      case 'IN_COMPLIANCE':
      case 'APPROVED':
      case 'SENT_TO_PROVIDER':
        return '#3B82F6'; // blue-500
      case 'RESOLVED':
        return '#10B981'; // green-500
      case 'REJECTED':
        return '#EF4444'; // red-500
      case 'CLOSED':
        return '#6B7280'; // gray-500
      default:
        return '#6B7280';
    }
  }

  // Helper to get priority color
  getPriorityColor(priority: TicketPriority): string {
    switch (priority) {
      case 'LOW':
        return '#10B981'; // green-500
      case 'MEDIUM':
        return '#EAB308'; // yellow-500
      case 'HIGH':
        return '#F97316'; // orange-500
      case 'CRITICAL':
        return '#EF4444'; // red-500
      default:
        return '#6B7280';
    }
  }

  // Helper to format relative time
  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  // Helper to format file size
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

export const supportService = new SupportService();